import { useCallback } from "react";
import { Platform, findNodeHandle } from "react-native";
const isView = (ref) => Boolean(ref.current?.measureLayout);
const isScrollView = (ref) => Boolean(ref.current?.scrollTo);
const isHtmlElement = (element) => typeof HTMLElement !== "undefined" && element instanceof HTMLElement;
// On web a ref may not point to a raw HTMLElement: react-native-web ScrollView,
// react-native-gesture-handler and react-native-keyboard-controller all wrap the
// scrollable node and expose it through `getScrollableNode()`. Resolve the real
// DOM node so scroll works regardless of which container the consumer mounts.
const resolveDomNode = (element) => {
    if (isHtmlElement(element)) {
        return element;
    }
    const scrollableNode = element?.getScrollableNode?.();
    return isHtmlElement(scrollableNode) ? scrollableNode : null;
};
const useScrollTo = ({ scrollRef }) => {
    const measure = useCallback(({ ref, callback }) => {
        if (!ref.current || !scrollRef?.current) {
            return;
        }
        if (!isView(ref)) {
            return;
        }
        const scrollContainer = scrollRef.current;
        const relativeNode = scrollContainer.getNativeScrollRef?.() ??
            scrollContainer.getScrollableNode?.() ??
            findNodeHandle(scrollContainer);
        if (relativeNode == null) {
            return;
        }
        ref.current.measureLayout(relativeNode, (left, top, width, height) => {
            callback({ top, height, left, width });
        }, () => undefined);
    }, [scrollRef]);
    const scrollTo = useCallback(({ ref, x, y, animated = true }) => {
        if (!ref.current || !scrollRef?.current) {
            return;
        }
        if (Platform.OS === "web") {
            const targetNode = resolveDomNode(ref.current);
            const containerNode = resolveDomNode(scrollRef?.current);
            if (!targetNode || !containerNode) {
                return;
            }
            const targetRect = targetNode.getBoundingClientRect();
            const containerRect = containerNode.getBoundingClientRect();
            const left = containerNode.scrollLeft + targetRect.left - containerRect.left + (x || 0);
            const top = containerNode.scrollTop + targetRect.top - containerRect.top + (y || 0);
            // react-native-web monkey-patches `scrollTo` onto the scroll node with the RN signature
            // ({ x, y, animated }), which ignores the DOM `{ top, left, behavior }` options and no-ops.
            // Call the native Element.prototype.scrollTo to bypass the patched instance method.
            const nativeScrollTo = typeof HTMLElement !== "undefined"
                ? HTMLElement.prototype.scrollTo
                : undefined;
            if (nativeScrollTo) {
                nativeScrollTo.call(containerNode, { behavior: animated ? "smooth" : "auto", left, top });
            }
            else {
                containerNode.scrollTop = top;
                containerNode.scrollLeft = left;
            }
            return;
        }
        if (!isView(ref))
            return;
        measure({
            ref,
            callback: ({ top, left }) => {
                const scrollX = left + (x || 0);
                const scrollY = top + (y || 0);
                if (isScrollView(scrollRef)) {
                    scrollRef.current?.scrollTo({ x: scrollX, y: scrollY, animated });
                }
            },
        });
    }, [measure, scrollRef]);
    return scrollTo;
};
export { useScrollTo, resolveDomNode };
